// src/components/Menu/MenuCard.js
import React, { useState } from 'react'; // Import useState
import { notifications } from '@mantine/notifications';
import {
  Text,
  Group,
  ThemeIcon,
  UnstyledButton,
  useMantineTheme,
  useMantineColorScheme,
  Box,
  Tooltip
} from '@mantine/core';
import { Link } from 'react-router-dom';

function MenuCard({ item }) {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const [isHovered, setIsHovered] = useState(false);

  // --- UPDATED: Check for both disabled and placeholder ---
  const isNonInteractive = item.type === 'placeholder' || item.type === 'disabled';

  const cardBackgroundColor = isDark ? theme.colors.dark[6] : theme.white;
  const titleTextColor = isDark ? theme.white : theme.black;
  const descriptionTextColor = isDark ? theme.colors.gray[4] : theme.colors.gray[7];
  const cardBorderColor = isDark ? theme.colors.dark[4] : theme.colors.gray[3];

  const handleClick = (e) => {
    if (isNonInteractive) {
      e.preventDefault(); // Prevent any navigation

      // Show a helpful notification instead of an alert
      notifications.show({
        title: item.type === 'disabled' ? 'Permission Required' : 'Coming Soon',
        message: item.type === 'disabled'
          ? "Requires permissions. Please contact your administrator."
          : `The "${item.title.split('|')[1].trim()}" feature is not yet available.`,
        color: 'blue',
        autoClose: 5000,
      });
    }
  };

  const boxBaseStyle = {
    padding: theme.spacing.lg,
    borderRadius: theme.radius.md,
    backgroundColor: cardBackgroundColor,
    border: `1px solid ${cardBorderColor}`,
    boxShadow: theme.shadows.md,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    color: titleTextColor,
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  };

  const boxHoverStyle = (!isNonInteractive && isHovered)
    ? {
      transform: 'translateY(-4px) scale(1.02)',
      boxShadow: theme.shadows.xl,
    }
    : {
      transform: 'none',
      boxShadow: theme.shadows.md,
    };

  const nonInteractiveStyleOverrides = isNonInteractive
    ? {
      opacity: 0.65,
      cursor: 'not-allowed',
    }
    : {};


  const unstyledButtonStyles = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    textDecoration: 'none',
    color: 'inherit',
    backgroundColor: 'transparent',
    padding: 0,
    borderRadius: 'inherit',
    textAlign: 'left',
  };

  const cardInnerContent = (
    <>
      <Group mb="md" align="center">
        <ThemeIcon variant="gradient" gradient={{ from: 'indigo', to: 'cyan' }} size={40} radius="md">
          <item.icon stroke={1.5} style={{ width: '24px', height: '24px' }} />
        </ThemeIcon>
        <Text
          fw={700}
          size="lg"
          style={{ flex: 1 }}
          c={titleTextColor}
        >
          {item.title}
        </Text>
      </Group>
      <Text
        size="sm"
        c={descriptionTextColor}
        style={{ flexGrow: 1, marginBottom: theme.spacing.md }}
      >
        {item.description}
      </Text>
    </>
  );

  // First, we define the card itself in a variable
  const cardElement = (
    <Box
      style={{
        ...boxBaseStyle,
        ...boxHoverStyle,
        ...nonInteractiveStyleOverrides
      }}
      onMouseEnter={() => { if (!isNonInteractive) setIsHovered(true); }}
      onMouseLeave={() => { if (!isNonInteractive) setIsHovered(false); }}
    >
      <UnstyledButton
        component={item.type === 'internal' ? Link : (item.type === 'external' ? 'a' : 'div')}
        {...(item.type === 'internal' && { to: item.linkTo })}
        {...(item.type === 'external' && { href: item.linkTo, target: '_blank', rel: 'noopener noreferrer' })}
        style={unstyledButtonStyles}
        onClick={handleClick}
      >
        {cardInnerContent}
      </UnstyledButton>
    </Box>
  );

  // Check if the card is non-interactive
  //const isNonInteractive = item.type === 'placeholder' || item.type === 'disabled';

  // If it is, decide which tooltip message to show
  if (isNonInteractive) {
    const tooltipLabel = item.type === 'disabled'
      ? "Requires permissions. Please contact your administrator."
      : "Feature coming soon!";

    return (
      <Tooltip
        label={tooltipLabel}
        withArrow
        position="bottom"
        multiline
        w={220}
        ta="center"
      >
        {cardElement}
      </Tooltip>
    );
  }

  // Otherwise, for all other types, return the card by itself.
  return cardElement;
}

export default MenuCard;